import axios from 'axios';
import { TENANT_HEADER, TENANT_STORAGE_KEY } from '../constants/tenancy';

const DB_NAME = 'cultivamais-front-offline';
const DB_VERSION = 1;
const CACHE_STORE = 'apiCache';
const QUEUE_STORE = 'mutationQueue';
const MUTATION_METHODS = ['post', 'put', 'patch', 'delete'];
const BLOCKED_MUTATION_PARTS = [
  '/auth/', '/tenants', '/assinaturas', '/perfil/foto', '/certificado',
  '/notafiscal', '/cte/', '/emitir', '/cancelar', '/validar',
  '/exportacoes-bancarias', '/senha', '/reset-password', '/forgot-password'
];

let dbPromise;
let syncing = false;
let listenersInstalled = false;

const now = () => new Date().toISOString();
const currentToken = () => localStorage.getItem('token') || sessionStorage.getItem('token') || '';

const tokenOwner = (token = currentToken()) => {
  if (!token) return '';
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return String(payload.nameid || payload.sub || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || '');
  } catch {
    return '';
  }
};

const openDb = () => {
  if (dbPromise) return dbPromise;
  if (!window.indexedDB) return Promise.reject(new Error('Armazenamento offline indisponível.'));

  dbPromise = new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(CACHE_STORE)) db.createObjectStore(CACHE_STORE, { keyPath: 'key' });
      if (!db.objectStoreNames.contains(QUEUE_STORE)) db.createObjectStore(QUEUE_STORE, { keyPath: 'localId' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
};

const withStore = async (storeName, mode, operation) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const request = operation(transaction.objectStore(storeName));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const put = (store, value) => withStore(store, 'readwrite', (target) => target.put(value));
const get = (store, key) => withStore(store, 'readonly', (target) => target.get(key));
const getAll = (store) => withStore(store, 'readonly', (target) => target.getAll());
const remove = (store, key) => withStore(store, 'readwrite', (target) => target.delete(key));

const normalizeUrl = (config = {}) => {
  const baseURL = config.baseURL || '';
  const url = config.url || '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
};

const normalizedPath = (config = {}) => String(config.url || '').split('?')[0].toLowerCase();

export const isMutationRequest = (config = {}) => MUTATION_METHODS.includes((config.method || 'get').toLowerCase());

export const isCacheableRequest = (config = {}) => {
  const method = (config.method || 'get').toLowerCase();
  const url = normalizedPath(config);
  return method === 'get'
    && !url.includes('/auth/')
    && !url.includes('/reset-password')
    && !url.includes('/forgot-password')
    && config.responseType !== 'blob';
};

export const canQueueMutation = (config = {}) => {
  const url = normalizedPath(config);
  const contentType = String(config.headers?.['Content-Type'] || config.headers?.['content-type'] || '').toLowerCase();
  return isMutationRequest(config)
    && !BLOCKED_MUTATION_PARTS.some((part) => url.includes(part))
    && !contentType.includes('multipart/form-data')
    && !(typeof FormData !== 'undefined' && config.data instanceof FormData)
    && config.responseType !== 'blob';
};

export const createRequestKey = (config = {}) => {
  const tenant = localStorage.getItem(TENANT_STORAGE_KEY) || '';
  const owner = tokenOwner();
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${owner}:${tenant}:${(config.method || 'get').toLowerCase()}:${normalizeUrl(config)}:${params}`;
};

export const saveCachedResponse = async (config, response) => {
  if (!isCacheableRequest(config)) return;
  try {
    await put(CACHE_STORE, {
      key: createRequestKey(config),
      data: response.data,
      status: response.status,
      savedAt: now(),
    });
  } catch {
    // Uma falha no cache nunca pode interromper uma resposta online válida.
  }
};

export const getCachedResponse = async (config) => {
  if (!isCacheableRequest(config)) return null;
  try {
    return await get(CACHE_STORE, createRequestKey(config));
  } catch {
    return null;
  }
};

const safeHeaders = (headers = {}) => {
  const output = {};
  Object.entries(headers).forEach(([key, value]) => {
    const lower = key.toLowerCase();
    if (['content-type', TENANT_HEADER.toLowerCase()].includes(lower) && typeof value === 'string') output[key] = value;
  });
  return output;
};

const randomId = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const getQueueItems = async () => {
  try {
    return await getAll(QUEUE_STORE);
  } catch {
    return [];
  }
};

export const getQueueCount = async () => {
  const owner = tokenOwner();
  const tenant = localStorage.getItem(TENANT_STORAGE_KEY) || '';
  const items = await getQueueItems();
  return items.filter((item) => item.owner === owner && item.tenantId === tenant).length;
};

const emitStatus = async (status = {}) => {
  const pending = await getQueueCount();
  window.dispatchEvent(new CustomEvent('cultivamais:offline-status', {
    detail: { online: navigator.onLine, pending, ...status },
  }));
};

export const enqueueMutation = async (config = {}) => {
  if (!canQueueMutation(config)) throw new Error('Esta operação exige conexão com a internet.');
  const item = {
    localId: randomId(),
    method: (config.method || 'post').toLowerCase(),
    url: config.url,
    baseURL: config.baseURL,
    data: config.data ?? null,
    params: config.params || null,
    headers: safeHeaders(config.headers || {}),
    tenantId: localStorage.getItem(TENANT_STORAGE_KEY) || '',
    owner: tokenOwner(),
    createdAt: now(),
    updatedAt: now(),
    attempts: 0,
    lastError: '',
  };
  await put(QUEUE_STORE, item);
  await emitStatus({ queued: true });
  return item;
};

export const syncQueuedMutations = async (baseURL) => {
  if (syncing || !navigator.onLine) {
    await emitStatus();
    return { synchronized: 0 };
  }

  const token = currentToken();
  const owner = tokenOwner(token);
  const tenant = localStorage.getItem(TENANT_STORAGE_KEY) || '';
  if (!token || !owner || !tenant) {
    await emitStatus();
    return { synchronized: 0 };
  }

  syncing = true;
  await emitStatus({ syncing: true, syncError: '' });
  let synchronized = 0;
  let syncError = '';

  const items = (await getQueueItems())
    .filter((item) => item.owner === owner && item.tenantId === tenant)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  for (const item of items) {
    try {
      await axios.request({
        baseURL: item.baseURL || baseURL,
        url: item.url,
        method: item.method,
        data: item.data,
        params: item.params || undefined,
        headers: {
          ...item.headers,
          Authorization: `Bearer ${token}`,
          [TENANT_HEADER]: item.tenantId,
        },
      });
      await remove(QUEUE_STORE, item.localId);
      synchronized += 1;
    } catch (error) {
      syncError = error.response?.data?.message || error.message || 'Erro ao sincronizar.';
      await put(QUEUE_STORE, {
        ...item,
        attempts: item.attempts + 1,
        updatedAt: now(),
        lastError: syncError,
      });
      break;
    }
  }

  syncing = false;
  await emitStatus({ syncing: false, synchronized, syncError });
  if (synchronized > 0) window.dispatchEvent(new CustomEvent('cultivamais:offline-synchronized', { detail: { synchronized } }));
  return { synchronized, syncError };
};

export const installOfflineListeners = (baseURL) => {
  if (listenersInstalled) return;
  listenersInstalled = true;
  const sync = () => syncQueuedMutations(baseURL);
  window.addEventListener('online', sync);
  window.addEventListener('offline', () => emitStatus({ syncing: false }));
  window.addEventListener('focus', () => { if (navigator.onLine) sync(); });
  window.setTimeout(() => {
    emitStatus();
    if (navigator.onLine) sync();
  }, 500);
};
